'use client'
import { createContext, useCallback, useContext, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from './AuthContext'
import FeatureUpgradeModal from '../component/FeatureUpgradeModal'
import { meetsFeaturePlan } from '../config/upgradeFeatures'

// App-wide upgrade gate.
//
// Exists because the feature gates are spread across the create panel, the video panel, the pose
// modal and chat, and the generic limit modal they all shared lives in Create.jsx — so anything
// outside that tree either threaded setIsLimitModalOpen/setErrorText/setLimitTitle down as props
// or (InputsVideo's Animate box) opened the modal with no title and no body at all. One provider
// holding one modal means a gate is a single call from anywhere.
//
// The modal is intentionally NOT a replacement for the generic limit modal: out-of-gems, server
// overload and moderation refusals are not features to buy, and they keep using the existing one.
// This is only for "your plan doesn't include this".
const UpgradeGateContext = createContext({
  openUpgrade: () => {},
  requireFeature: () => true,
  closeUpgrade: () => {},
})

export const useUpgradeGate = () => useContext(UpgradeGateContext)

export const UpgradeGateProvider = ({ children }) => {
  const router = useRouter()
  const { user, isPlusOrHigher, isPremiumOrHigher, isStudioOrHigher } =
    useAuth()
  const [gate, setGate] = useState(null) // { featureId, secondary, overrides }
  // Open state is tracked separately from `gate` so the panel still has content to render while
  // it fades out — clearing `gate` on close would unmount the modal mid-transition and make it
  // vanish, which no other dialog in the app does.
  const [isOpen, setIsOpen] = useState(false)

  const closeUpgrade = useCallback(() => setIsOpen(false), [])

  // Show the modal for `featureId`. `secondary` is an optional { label, onClick } — a way out
  // that isn't a purchase (e.g. "Use a preset face"), which converts better than a dead end and
  // is honest when a free path genuinely exists.
  // `overrides` shallow-merges over the registry entry. It exists for gates whose tier is DATA
  // rather than code — a pose's required plan comes from the loras table — and is deliberately
  // awkward to reach for: everything else should get its own registry entry so the copy has one
  // home.
  const openUpgrade = useCallback(
    (featureId, { secondary, overrides } = {}) => {
      setGate({ featureId, secondary, overrides })
      setIsOpen(true)
    },
    [],
  )

  // The gate itself: true = allowed, false = handled (modal shown, or routed to signup).
  //
  //   if (!requireFeature('aiCharacter')) return
  //
  // Logged-out users go to signup instead: they have no plan to upgrade, and a paywall shown
  // before an account exists asks for the second step first.
  const requireFeature = useCallback(
    (featureId, options = {}) => {
      if (!user) {
        router.push('/signup')
        return false
      }
      const allowed = meetsFeaturePlan(featureId, {
        isPlusOrHigher,
        isPremiumOrHigher,
        isStudioOrHigher,
      })
      if (allowed) return true
      openUpgrade(featureId, options)
      return false
    },
    [
      user,
      router,
      isPlusOrHigher,
      isPremiumOrHigher,
      isStudioOrHigher,
      openUpgrade,
    ],
  )

  return (
    <UpgradeGateContext.Provider
      value={{ openUpgrade, requireFeature, closeUpgrade }}
    >
      {children}
      <FeatureUpgradeModal
        featureId={gate?.featureId}
        overrides={gate?.overrides}
        isOpen={isOpen}
        closeModal={closeUpgrade}
        onUpgrade={() => router.push('/upgrade')}
        secondary={
          gate?.secondary && {
            label: gate.secondary.label,
            // <Modal> already closes itself before firing this, which drives our own
            // isOpen through closeModal — so just run the action.
            onClick: () => gate.secondary.onClick?.(),
          }
        }
      />
    </UpgradeGateContext.Provider>
  )
}
